// panel/src/context/VoiceContext.js
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Device } from "@twilio/voice-sdk";
import { adminApi } from "services/api";
import { useAuth } from "./AuthContext";

const VoiceContext = createContext(null);

export const useVoice = () => useContext(VoiceContext);

export const VoiceProvider = ({ children }) => {
  const { isAuthenticated, hasPermission } = useAuth();
  const [status, setStatus] = useState("unavailable");
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [muted, setMuted] = useState(false);
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  // Mirrors `status` for the "incoming" device listener below, which is
  // registered once (inside an effect with an empty-ish dependency array) and
  // so can't close over a fresh `status` on every render — it reads this ref
  // instead to see the CURRENT status when a new call rings in.
  const statusRef = useRef(status);

  const canUseVoice = isAuthenticated && hasPermission && hasPermission("voice-calls");

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (!canUseVoice) {
      deviceRef.current?.destroy();
      deviceRef.current = null;
      setStatus("unavailable");
      return undefined;
    }

    let cancelled = false;
    let refreshTimer;

    const setup = async () => {
      const { token } = await adminApi.getVoiceToken();
      if (cancelled) return;

      const device = new Device(token, { logLevel: "error" });
      deviceRef.current = device;

      device.on("registered", () => setStatus((s) => (s === "unavailable" ? "idle" : s)));
      device.on("unregistered", () => setStatus("unavailable"));
      device.on("error", (err) => console.error("Twilio Device error:", err));

      device.on("incoming", (call) => {
        // A second inbound call while already on one would otherwise clobber
        // callRef to point at the new ringing call (breaking hangUp/toggleMute
        // for the still-live call) and switch the UI to Accept/Decline,
        // hiding the active call's controls. Reject it outright instead.
        if (statusRef.current === "in-call" || statusRef.current === "ringing-out") {
          call.reject();
          return;
        }
        callRef.current = call;
        setIncomingCall(call);
        setStatus("ringing-in");
        call.on("cancel", () => {
          setIncomingCall(null);
          setStatus("idle");
          callRef.current = null;
        });
        call.on("disconnect", () => {
          setIncomingCall(null);
          setActiveCall(null);
          setStatus("idle");
          callRef.current = null;
        });
      });

      await device.register();
      if (cancelled) return;
      refreshTimer = setInterval(async () => {
        try {
          const { token: fresh } = await adminApi.getVoiceToken();
          device.updateToken(fresh);
        } catch (err) {
          console.error("Failed to refresh Twilio Voice token:", err);
          // The Device's token will eventually expire and inbound calls will
          // silently stop working — surface that instead of failing silently.
          setStatus("error");
        }
      }, 55 * 60 * 1000); // refresh before the 1hr TTL
    };

    setup().catch((err) => {
      console.error("Failed to set up Twilio Voice device:", err);
      setStatus("error");
    });

    return () => {
      cancelled = true;
      clearInterval(refreshTimer);
      deviceRef.current?.destroy();
      deviceRef.current = null;
    };
  }, [canUseVoice]);

  const startCall = useCallback(async (phoneNumber) => {
    if (!deviceRef.current) return;
    setStatus("ringing-out");
    try {
      // device.connect() returns a Promise<Call>, not a Call — awaiting it is
      // required. Using the unresolved promise directly (as this used to)
      // silently breaks everything downstream: `call.on(...)` throws because
      // a Promise has no `.on`, so no accept/disconnect/cancel listeners ever
      // attach, and hangUp()/toggleMute() then call methods that don't exist
      // on a Promise — the underlying Twilio call still connects fine (the
      // connect request itself doesn't need the resolved value), so only the
      // controls silently stop working, not the call itself.
      const call = await deviceRef.current.connect({ params: { To: phoneNumber } });
      callRef.current = call;
      setActiveCall(call);
      call.on("accept", () => setStatus("in-call"));
      call.on("disconnect", () => {
        setActiveCall(null);
        setStatus("idle");
        callRef.current = null;
      });
      call.on("cancel", () => {
        setActiveCall(null);
        setStatus("idle");
        callRef.current = null;
      });
    } catch (err) {
      console.error("Failed to start outbound call:", err);
      setStatus("idle");
    }
  }, []);

  const acceptCall = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.accept();
    setActiveCall(incomingCall);
    setIncomingCall(null);
    setStatus("in-call");
  }, [incomingCall]);

  const rejectCall = useCallback(() => {
    incomingCall?.reject();
    setIncomingCall(null);
    setStatus("idle");
  }, [incomingCall]);

  const hangUp = useCallback(() => {
    callRef.current?.disconnect();
  }, []);

  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    const next = !muted;
    callRef.current.mute(next);
    setMuted(next);
  }, [muted]);

  return (
    <VoiceContext.Provider
      value={{
        status,
        incomingCall,
        activeCall,
        startCall,
        acceptCall,
        rejectCall,
        hangUp,
        toggleMute,
        muted,
      }}
    >
      {children}
    </VoiceContext.Provider>
  );
};

VoiceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
